import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { Tenant } from '@hyvay/shared-types';
import { tenantService } from '../services/tenant.service';
import { studioService, getPublishedState, type PublishedState } from '../services/studio.service';
import { publishProject } from '../services/projectStream.service';
import { StudioHeader, type Device, type StudioTab } from '../components/studio/StudioHeader';
import { PublishDialog } from '../components/studio/PublishDialog';
import { DomainDialog } from '../components/studio/DomainDialog';
import { CodeStudio } from '../components/studio/CodeStudio';
import { SEOHead } from '../components/seo/SEOHead';
import { PUBLISH_COST, readTheme, writeTheme, type Theme } from '../components/studio/studioUi';
import { TopUpDialog, type SettleStatus } from './landing/TopUpDialog';
import { BootOverlay, useBootOverlay } from './landing/BootOverlay';
import { STUDIO_FONT_SANS as FONT_SANS } from './landing/landingData';
import { useToast } from '../components/common/Toast';
import { trackAppPublish } from '../components/analytics/GoogleAnalytics';
import './landing/landing.css';

const SUPPORT_EMAIL = 'support@hyvay.com';

/** mailto: with a short guided template so support gets the context it needs. */
function helpMailto(projectName: string, projectId: string): string {
    const subject = `HyVay Studio help — ${projectName}`;
    const body = [
        'Hi HyVay team,',
        '',
        `Project: ${projectName}`,
        `Project id: ${projectId}`,
        `Studio link: ${window.location.origin}/studio/${projectId}`,
        '',
        'What I was trying to do:',
        '',
        'What happened instead:',
        '',
        'Browser and device:',
        '',
    ].join('\n');
    return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

/**
 * Studio shell — the handoff design's "HyVay Studio" page. Hosts the top bar,
 * the publish and top-up dialogs and the theme, and hands the editing surface to
 * CodeStudio (chat + WebContainer live preview + Monaco over the app's git-backed
 * workspace).
 */
export const Studio: React.FC = () => {
    const { tenantId } = useParams<{ tenantId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [loading, setLoading] = useState(true);
    const [theme, setTheme] = useState<Theme>(readTheme);
    const [tab, setTab] = useState<StudioTab>('preview');
    const [device, setDevice] = useState<Device>('desktop');
    const [credits, setCredits] = useState(0);
    const [published, setPublished] = useState<PublishedState | null>(null);
    const [publishOpen, setPublishOpen] = useState(false);
    const [domainOpen, setDomainOpen] = useState(false);
    const [topUpOpen, setTopUpOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const toast = useToast();

    // Prompt handed over from the landing "describe your app" flow. Stored in
    // sessionStorage (survives the full-page navigation into /studio). The
    // initializer must be PURE — StrictMode invokes it twice in dev.
    const [initialPrompt] = useState<string | undefined>(() => {
        if (!tenantId) return undefined;
        return sessionStorage.getItem(`hyvay:initial-prompt:${tenantId}`) ?? location.state?.initialPrompt;
    });

    // Consume the stored prompt exactly once so a refresh can't re-trigger the
    // generation (and re-charge a credit). Only when the tab is cross-origin
    // isolated — otherwise CodeStudio reloads the page to pick up the COOP/COEP
    // headers and the prompt must survive that reload.
    useEffect(() => {
        if (tenantId && (window as any).crossOriginIsolated === true) {
            sessionStorage.removeItem(`hyvay:initial-prompt:${tenantId}`);
        }
    }, [tenantId]);

    const showNotice = useCallback((tone: 'ok' | 'warn' | 'bad', text: string) => {
        toast.show(text, { tone: tone === 'ok' ? 'success' : tone === 'warn' ? 'warn' : 'error' });
    }, [toast]);

    /** Re-read the authoritative balance. A turn is only charged when it succeeds,
     *  so decrementing locally would drift. */
    const refreshCredits = useCallback(async () => {
        const res = await studioService.getCredits();
        if (res.success) setCredits(res.data.credits);
    }, []);

    const refreshPublished = useCallback(async () => {
        if (!tenantId) return;
        setPublished(await getPublishedState(tenantId));
    }, [tenantId]);

    useEffect(() => {
        if (!tenantId) return;
        let cancelled = false;
        void (async () => {
            setLoading(true);
            const response = await tenantService.getTenantById(tenantId);
            if (cancelled) return;
            if (response.success) {
                setTenant(response.data);
                await Promise.all([refreshCredits(), refreshPublished()]);
            } else {
                navigate('/');
            }
            setLoading(false);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tenantId]);

    const changeTheme = (next: Theme) => {
        writeTheme(next);
        setTheme(next);
    };

    const handleRename = async (name: string) => {
        if (!tenant) return;
        const previous = tenant.name;
        setTenant({ ...tenant, name });
        const res = await tenantService.updateTenant(tenant.id, { name });
        if (!res.success) {
            setTenant((t) => (t ? { ...t, name: previous } : t));
            showNotice('bad', 'Could not rename the project. Please try again.');
        }
    };

    const handleExport = async () => {
        if (!tenant || exporting) return;
        setExporting(true);
        try {
            const blob = await studioService.exportApplication(tenant.id);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${(tenant.name || 'hyvay-app').replace(/[^a-zA-Z0-9-_]+/g, '-')}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed', e);
            showNotice('bad', 'Export failed. Please try again.');
        } finally {
            setExporting(false);
        }
    };

    const handleConfirmPublish = async (subdomain: string) => {
        if (!tenant) return { success: false, error: 'Project not found' };
        // Streaming publish: the server npm-installs + vite-builds the workspace
        // and pushes the `published` branch — takes a while, so consume SSE.
        return new Promise<{ success: boolean; url?: string; error?: string }>((resolve) => {
            publishProject(
                { applicationId: tenant.id, subdomain },
                {
                    onLog: (line) => console.log('[publish]', line),
                    onDone: (data) => {
                        trackAppPublish();
                        setCredits((prev) => Math.max(0, prev - PUBLISH_COST));
                        void refreshCredits();
                        resolve({ success: true, url: data.url });
                    },
                    onError: (message) => resolve({ success: false, error: message || 'Failed to publish the site' }),
                },
            ).catch((e) => resolve({ success: false, error: e?.message || 'Failed to publish the site' }));
        });
    };

    const onTopUpSettled = (status: SettleStatus, message: string) => {
        showNotice(status === 'SUCCESS' ? 'ok' : status === 'PENDING' ? 'warn' : 'bad', message);
        void refreshCredits();
    };

    // Full-bleed shell (2026-11 design): no desk padding / inset card any more.
    const shell: React.CSSProperties = { height: '100vh', padding: 0, background: 'var(--hv-page)', fontFamily: FONT_SANS, color: 'var(--hv-ink)', WebkitFontSmoothing: 'antialiased' };
    const card: React.CSSProperties = { height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--hv-page)' };

    // The design's launch overlay, carried over from the landing/account click:
    // stays until 2.2 s have passed since the click AND the project is loaded,
    // while the studio (and the WebContainer boot) already run underneath.
    const boot = useBootOverlay(!loading && !!tenant);

    if (loading || !tenant) {
        return (
            <div className="hv-root hv-st" data-hv-theme={theme} style={shell}>
                <div style={card} />
                <BootOverlay copy={boot?.copy ?? 'OPENING YOUR PROJECT'} dark={theme === 'dark'} />
            </div>
        );
    }

    return (
        <div className="hv-root hv-st" data-hv-theme={theme} style={shell}>
            <SEOHead title={`Studio - ${tenant.name}`} noindex={true} />
            <div style={card}>
                <StudioHeader
                    projectName={tenant.name}
                    onRename={(name) => void handleRename(name)}
                    tab={tab}
                    onTabChange={setTab}
                    shareUrl={published?.isActive ? published.url : null}
                    onExport={() => void handleExport()}
                    exporting={exporting}
                    helpHref={helpMailto(tenant.name, tenant.id)}
                    published={!!published?.isActive}
                    onPublish={() => setPublishOpen(true)}
                    theme={theme}
                    onTheme={changeTheme}
                />

                <CodeStudio
                    applicationId={tenant.id}
                    pageId={tenant.pages?.[0]?.id}
                    initialPrompt={initialPrompt}
                    activeTab={tab}
                    viewport={device}
                    onViewportChange={setDevice}
                    onOpenMedia={() => setTab('media')}
                    theme={theme}
                    initialHistory={(tenant as any).chatHistory ?? []}
                    credits={credits}
                    onCreditsMaybeChanged={refreshCredits}
                    onOpenTopUp={() => setTopUpOpen(true)}
                />
            </div>

            {boot && <BootOverlay copy={boot.copy} hiding={boot.hiding} dark={theme === 'dark'} />}

            <PublishDialog
                open={publishOpen}
                onOpenChange={setPublishOpen}
                onPublish={handleConfirmPublish}
                credits={credits}
                applicationId={tenant.id}
                onTopUp={() => setTopUpOpen(true)}
                onPublished={() => void refreshPublished()}
                onOpenDomains={() => { setPublishOpen(false); setDomainOpen(true); }}
            />
            <DomainDialog open={domainOpen} onClose={() => setDomainOpen(false)} applicationId={tenant.id} onChanged={() => void refreshPublished()} />
            <TopUpDialog open={topUpOpen} onClose={() => setTopUpOpen(false)} onSettled={onTopUpSettled} />
        </div>
    );
};
